/**
 * Users Management Page
 * Main page component for user management with full CRUD operations
 */

import React, { useState } from "react";
import Drawer from "../../components/drawer/drawer";
import Modal from "../../components/modal/modal";
import PageHeader from "../../components/page-header/page-header";
import { UserEntity, UserManagementState } from "./types/user.types";
import { UserForm, UserTable } from "./components";
import { useUsers } from "./hooks/use-users";
import {
  resetUserState,
  openEditDrawer,
  openCreateDrawer,
  closeDrawer,
  openDeleteModal,
  closeDeleteModal,
  getDrawerTitle,
} from "./utils/user-utils";
import "./users.css";
import { toast } from "sonner";

/**
 * Main Users page component
 * Manages user list, creation, editing, and deletion
 */
const Users: React.FC = () => {
  const [state, setState] = useState<UserManagementState>(resetUserState());

  const {
    users,
    isLoading,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleToggleActive,
    // Pagination from hook
    currentPage,
    itemsPerPage,
    totalPages,
    handlePageChange,
    handleItemsPerPageChange,
  } = useUsers();

  // TODO: Improve error handling and add proper response types
  /**
   * Handle form submission for create/edit
   */
  const handleSubmit = async (values: Partial<UserEntity>): Promise<void> => {
    let response: any;
    if (state.editingUser) {
      response = await handleUpdateUser(state.editingUser.id, values);
    } else {
      response = await handleCreateUser(values);
    }

    const hasSuccessField = typeof response?.success === "boolean";
    if (!hasSuccessField || response?.success) {
      toast.success(response.message);
      setState(closeDrawer());
    } else {
      toast.error(response.message);
    }
  };

  /**
   * Handle user deletion
   */
  const handleDelete = async (): Promise<void> => {
    if (!state.deletingUser) return;

    const success = await handleDeleteUser(state.deletingUser.id);
    if (success) {
      setState(closeDeleteModal());
    }
  };

  /**
   * Handle toggle active status
   */
  const handleToggleActiveStatus = async (user: UserEntity): Promise<void> => {
    await handleToggleActive(user.id);
  };

  /**
   * Open edit drawer
   */
  const handleEditDrawer = (user: UserEntity): void => {
    setState(openEditDrawer(user));
  };

  /**
   * Open create drawer
   */
  const handleCreateDrawer = (): void => {
    setState(openCreateDrawer());
  };

  /**
   * Close drawer
   */
  const handleCloseDrawer = (): void => {
    setState(closeDrawer());
  };

  /**
   * Open delete modal
   */
  const handleDeleteModal = (user: UserEntity): void => {
    setState(openDeleteModal(user));
  };

  /**
   * Close delete modal
   */
  const handleCloseDeleteModal = (): void => {
    setState(closeDeleteModal());
  };

  return (
    <div className="users">
      <PageHeader
        title="Пользователи"
        subtitle="Управление пользователями системы"
        actionButton={{
          text: "Добавить пользователя",
          icon: "plus",
          onClick: handleCreateDrawer,
        }}
      />

      <UserTable
        users={users}
        isLoading={isLoading}
        onEdit={handleEditDrawer}
        onDelete={handleDeleteModal}
        onToggleActive={handleToggleActiveStatus}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Create/Edit Drawer */}
      <Drawer
        title={getDrawerTitle(state.editingUser)}
        isOpen={state.isDrawerOpen}
        onClose={handleCloseDrawer}
        width={600}
      >
        <UserForm
          initialValues={state.editingUser}
          onSubmit={handleSubmit}
          onCancel={handleCloseDrawer}
          isLoading={isLoading}
        />
      </Drawer>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={state.isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Подтвердите удаление"
        onConfirm={handleDelete}
      >
        <p>Вы уверены, что хотите удалить этого пользователя?</p>
        {state.deletingUser && (
          <div className="users__delete-info">
            <p>
              <strong>
                {state.deletingUser.telegramFirstName &&
                state.deletingUser.telegramLastName
                  ? `${state.deletingUser.telegramFirstName} ${state.deletingUser.telegramLastName}`
                  : state.deletingUser.telegramFirstName ||
                    state.deletingUser.telegramLastName ||
                    "Пользователь"}
              </strong>
            </p>
            <p className="users__delete-email">
              {state.deletingUser.email || "—"}
            </p>
            <p className="users__delete-warning">
              Это действие нельзя отменить. Все связанные данные будут
              сохранены.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Users;
